import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Noms des effets sonores disponibles dans l'application.
 */
export type SfxName = 'selection' | 'submit' | 'cheat' | 'congratulation' | 'timer_warning';

export type SfxPlayOptions = {
  maxDurationMs?: number;
};

/** Options du clic de sélection / soumission (troncature identique). */
export const SFX_SELECTION_CLICK_OPTIONS: SfxPlayOptions = { maxDurationMs: 350 };

/**
 * Service de gestion des effets sonores (SFX).
 *
 * Les fichiers audio sont pré-chargés une seule fois via HTMLAudioElement
 * et mis en cache en mémoire. Chaque appel à play() réutilise le même
 * objet Audio (reset currentTime → pas de re-téléchargement).
 *
 * Usage :
 *   - sfxService.preload('selection')   → télécharge le fichier en avance
 *   - sfxService.play('selection')      → joue le son (précharge auto si nécessaire)
 */
@Injectable()
export class SfxService {
  private readonly BASE_URL = environment.media + 'ia/son/';

  /** Mapping nom logique → nom de fichier */
  private readonly SFX_MAP: Record<SfxName, string> = {
    selection: 'selection_de_reponse.wav',
    submit: 'soumission_reponse.wav',
    cheat: 'pop-up_triche.wav',
    congratulation: 'page_de_felicitation.wav',
    timer_warning: 'alerte_temps.wav',
  };

  /** Timeouts de troncature pour play() avec maxDurationMs */
  private playTimeouts = new Map<SfxName, ReturnType<typeof setTimeout>>();

  /** Cache des HTMLAudioElement préchargés */
  private cache = new Map<SfxName, HTMLAudioElement>();

  /** Son en attente si l'autoplay est bloqué (navigateur sans interaction) */
  private pendingAutoplay: SfxName | null = null;
  private interactionListenerBound: (() => void) | null = null;

  /**
   * Précharge un son dans le cache (crée le HTMLAudioElement et démarre le téléchargement).
   * Ne fait rien si déjà en cache.
   */
  preload(name: SfxName): void {
    if (this.cache.has(name)) return;

    const audio = new Audio(this.BASE_URL + this.SFX_MAP[name]);
    audio.preload = 'auto';
    audio.load();
    this.cache.set(name, audio);
  }

  /**
   * Précharge plusieurs sons d'un coup.
   */
  preloadMany(...names: SfxName[]): void {
    names.forEach(name => this.preload(name));
  }

  /**
   * Joue un effet sonore.
   * Le son est préchargé automatiquement s'il ne l'est pas encore.
   * Si le son est déjà en cours de lecture, il est redémarré depuis le début.
   *
   * Si le navigateur bloque l'autoplay (pas d'interaction utilisateur),
   * le son est mis en attente et joué automatiquement dès la première interaction.
   */
  play(name: SfxName, options?: SfxPlayOptions): void {
    if (!this.cache.has(name)) {
      this.preload(name);
    }

    const audio = this.cache.get(name)!;
    this.clearPlayTimeout(name);
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Autoplay bloqué → mettre en attente et jouer au premier clic/touche
      this.pendingAutoplay = name;
      this.listenForInteraction();
    });

    if (options?.maxDurationMs && options.maxDurationMs > 0) {
      const timeoutId = setTimeout(() => {
        audio.pause();
        this.playTimeouts.delete(name);
      }, options.maxDurationMs);
      this.playTimeouts.set(name, timeoutId);
    }
  }

  /** Annule la troncature en cours pour un son donné. */
  private clearPlayTimeout(name: SfxName): void {
    const timeoutId = this.playTimeouts.get(name);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.playTimeouts.delete(name);
    }
  }

  /**
   * Enregistre un listener one-shot sur la première interaction utilisateur
   * pour débloquer l'autoplay et jouer le son en attente.
   */
  private listenForInteraction(): void {
    if (this.interactionListenerBound) return; // déjà en écoute

    this.interactionListenerBound = () => {
      if (this.pendingAutoplay) {
        const name = this.pendingAutoplay;
        this.pendingAutoplay = null;
        this.playImmediate(name);
      }
      this.removeInteractionListener();
    };

    ['click', 'touchstart', 'keydown'].forEach(event =>
      document.addEventListener(event, this.interactionListenerBound!, { once: true, passive: true })
    );
  }

  /**
   * Supprime les listeners d'interaction.
   */
  private removeInteractionListener(): void {
    if (this.interactionListenerBound) {
      ['click', 'touchstart', 'keydown'].forEach(event =>
        document.removeEventListener(event, this.interactionListenerBound!)
      );
      this.interactionListenerBound = null;
    }
  }

  /**
   * Joue un son sans fallback (utilisé après déblocage autoplay).
   */
  private playImmediate(name: SfxName): void {
    if (!this.cache.has(name)) {
      this.preload(name);
    }
    const audio = this.cache.get(name)!;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  /**
   * Joue un son en boucle (loop natif HTMLAudioElement).
   * Ne relance pas si déjà en cours de lecture.
   */
  loop(name: SfxName): void {
    if (!this.cache.has(name)) {
      this.preload(name);
    }
    const audio = this.cache.get(name)!;
    if (!audio.paused && audio.loop) return; // déjà en boucle
    audio.loop = true;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  /**
   * Arrête la boucle d'un son et le remet au début.
   */
  stopLoop(name: SfxName): void {
    const audio = this.cache.get(name);
    if (audio) {
      audio.loop = false;
      audio.pause();
      audio.currentTime = 0;
    }
  }

  /**
   * Libère un ou plusieurs sons du cache (pause + suppression).
   * Utile pour éviter qu'un son continue d'être jouable après un changement de contexte.
   */
  dispose(...names: SfxName[]): void {
    const toDispose = names.length > 0 ? names : Array.from(this.cache.keys());
    toDispose.forEach(name => {
      const audio = this.cache.get(name);
      if (audio) {
        audio.pause();
        audio.src = '';
        this.cache.delete(name);
      }
    });

    // Si on dispose tout, nettoyer aussi le pending autoplay
    if (names.length === 0) {
      this.pendingAutoplay = null;
      this.removeInteractionListener();
    }
  }
}
